import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core'
import {
  GetPagedTasksGQL,
  TaskStatus,
  SortEnumType,
  Task,
  GetProductTransactionsGQL,
  ProductItemTransaction,
  ProductItemTransactionType,
  ConsumedProductItemsGQL,
} from '@ziptility/graphql'
import {
  EBvFieldType,
  FeatureAPIService,
  FileAPIService,
  IBvComment,
  IBvFeature,
  IBvLayer,
  IBvNotification,
  NotificationAPIService,
} from '@ziptility/modules'
import { BvAlertService } from '@ziptility/bv-alert'
import { BoardTask } from '@ziptility/tasks/boards/boards.store'
import { getTaskDueDateUrgency, makeTaskDateLabel, makeTaskDueDateLabel } from '@ziptility/utils/format'
import { filter as rxFilter, firstValueFrom, map as rxMap, Subscription } from 'rxjs'
import moment from 'moment'
import { Routine, RoutineService } from '@ziptility/routines'
import {
  ActivityNotificationSocket,
  AuthorizationService,
  CommonService,
  FormControlService,
  MapActivityDataSource,
  getMapboxImageUrl,
} from '@ziptility/services'
import { FormGroup } from '@angular/forms'
import { BvCustomFieldControl } from '@ziptility/components/custom-fields'
import { Store } from '@ngrx/store'
import { ClipboardService } from 'ngx-clipboard'
import { entries, filter, orderBy, map, flatten } from 'lodash/fp'
import { DialogService } from '@ziptility/components/dialogs'
import { enrichNotification } from '@ziptility/modules/bv-api/services/notifications.service'
import { MutableProductSelection } from '@ziptility/inventory/product-selector/selection.store'
import { EditorStore } from '@ziptility/editor'
import { AssetPopupService } from '@ziptility/map/map/asset-popup.service'
import { toSignal } from '@angular/core/rxjs-interop'
import { getLayers } from '@ziptility/store'

export const taskToBoardTask = (t: Task) => {
  return {
    ...t,
    dueDateUrgency: getTaskDueDateUrgency(t.dueDate, t.status),
    formattedDateLabel: makeTaskDateLabel(t),
    formattedDueDate: makeTaskDueDateLabel(t.dueDate),
  }
}

const commentSort = (a: IBvComment, b: IBvComment) => {
  return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
}

const makeVariablesForStatus = (orgId: string, featureId: string, status: TaskStatus) => {
  return {
    where: {
      orgId: {
        eq: orgId,
      },
      status: {
        eq: status,
      },
      asset: {
        id: {
          eq: featureId,
        },
      },
    },
    order: [
      status == TaskStatus.Open
        ? {
            order: SortEnumType.Asc,
          }
        : {
            dateCompleted: SortEnumType.Desc,
          },
    ],
  }
}

export type ProductItemDetails = {
  id: string
  name: string
  sku: string | undefined
  manufacturer: string | undefined
  size: string | undefined
  imageId: string | undefined
  quantity: number
  quantityDescription: string
  isOutOfStock: boolean
  date: string
}

const toProductItemDetails = (transaction: ProductItemTransaction): ProductItemDetails => {
  const { id, transactionDate, quantity } = transaction
  const {
    product: { name, sku, manufacturer, size, image },
  } = transaction.productItem
  return {
    id,
    name,
    sku,
    manufacturer: manufacturer ?? '---',
    size: size ?? '---',
    imageId: image?.id,
    quantity,
    isOutOfStock: quantity <= 0,
    quantityDescription: `${Math.abs(quantity)} used on ${moment(transactionDate).format('MM/DD/YY')}`,
    date: transactionDate,
  }
}

@Injectable({ providedIn: 'root' })
export class AssetProfileService {
  readonly store = inject(Store)
  readonly layers = toSignal(this.store.select(getLayers))
  readonly featureService = inject(FeatureAPIService)
  readonly _getTasks = inject(GetPagedTasksGQL)
  readonly getProductTransactions = inject(GetProductTransactionsGQL)
  readonly routineService = inject(RoutineService)
  readonly formControlService = inject(FormControlService)
  readonly alertService = inject(BvAlertService)
  readonly commonService = inject(CommonService)
  readonly clipboardService = inject(ClipboardService)
  readonly authorizationService = inject(AuthorizationService)
  readonly dialogService = inject(DialogService)
  readonly fileAPIService = inject(FileAPIService)
  readonly socket = inject(ActivityNotificationSocket)
  readonly notificationService = inject(NotificationAPIService)
  readonly consumeProductItems = inject(ConsumedProductItemsGQL)
  readonly editorStore = inject(EditorStore)
  readonly popupService = inject(AssetPopupService)

  layer: WritableSignal<(IBvLayer & { singularTitle: string }) | undefined> = signal(undefined)
  feature: WritableSignal<IBvFeature | undefined> = signal(undefined)
  form: FormGroup | undefined
  formChangesSub: Subscription

  readonly disabled = computed(() => {
    if (this.saving()) {
      return true
    }

    if (!this.feature().dateCreated) {
      return false
    } else {
      return !this.form?.dirty
    }
  })

  readonly propertyControls: WritableSignal<BvCustomFieldControl[]> = signal([])
  readonly controls = this.propertyControls.asReadonly()
  readonly tasks: WritableSignal<Task[]> = signal([])
  readonly loadingTasks: WritableSignal<boolean> = signal(false)
  readonly selectedTaskStatus: WritableSignal<TaskStatus> = signal(TaskStatus.Open)
  readonly products: WritableSignal<ProductItemDetails[]> = signal([])
  readonly comments: WritableSignal<IBvComment[]> = signal([])
  readonly routines: WritableSignal<Routine[]> = signal([])
  readonly selectedRoutines: WritableSignal<Routine[]> = signal([])
  readonly notifications: WritableSignal<IBvNotification[]> = signal([])
  readonly hasUnsavedChanges = signal(false)
  readonly saving = signal(false)

  readonly selectableRoutines: Signal<Routine[]> = computed(() => {
    const selectedIds = this.selectedRoutines().map((r) => r.id)
    return this.routines().filter((r) => !selectedIds.includes(r.id))
  })

  readonly visibleTasks: Signal<Task[]> = computed(() => {
    return this.tasks().filter((t) => t.status === this.selectedTaskStatus())
  })

  readonly previewImageUrl: Signal<string | undefined> = computed(() => {
    const feature = this.feature()
    if (!feature) {
      return undefined
    }
    const geometry = this.feature().geometry
    let lat: number, lng: number
    if (geometry.type == 'Point') {
      lat = geometry.coordinates[1]
      lng = geometry.coordinates[0]
    } else {
      lat = geometry.coordinates[0][1]
      lng = geometry.coordinates[0][0]
    }

    const url = getMapboxImageUrl(lat, lng, 16, 1000, 200, {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry,
          properties: {},
        },
      ],
    })
    return `url('${url}')`
  })

  readonly locationIdentifier = computed(() => {
    return this.feature()?.locationIdentifier ?? ''
  })

  readonly systemId = computed(() => {
    return this.feature()?.systemId ?? ''
  })

  readonly layerTitle = computed(() => {
    const layerId = this.feature()?.layerId
    if (!layerId) {
      return ''
    }

    return this.layer().singularTitle
  })

  readonly numOpenTasks = computed(() => {
    return this.feature()?.numOpenTasks ?? 0
  })

  readonly canEditFeature = computed(() => {
    const layerId = this.feature()?.layerId
    if (!layerId) {
      return false
    }
    return !!this.authorizationService.canManageLayer(layerId)
  })

  featureHistory: MapActivityDataSource

  async load({
    userId,
    organizationId,
    mapId,
    featureId,
    hasDispatch,
    hasInventory,
  }: {
    userId: string
    organizationId: string
    mapId: string
    featureId: string
    hasDispatch: boolean
    hasInventory: boolean
  }) {
    const feature: IBvFeature = await firstValueFrom(this.featureService.get(featureId))
    const layer = this.layers().find((l) => l.id === feature.layerId)

    if (!layer) {
      console.log('could not find layer for feature')
      return
    }

    this.setFeature(feature, layer)

    if (hasDispatch) {
      this.loadTasksForStatus(organizationId, featureId, this.selectedTaskStatus())
    }
    if (hasInventory) {
      this.loadProducts(featureId)
    }

    this.loadRoutines(organizationId, mapId, featureId)
    this.loadComments(featureId)

    this.featureHistory = new MapActivityDataSource(
      this.notificationService,
      this.socket.feature.pipe(
        rxFilter(this.socket.isRelatedFeature(featureId)),
        rxMap((n) => enrichNotification(n, userId)),
      ),
      {
        userId,
        organizationId,
        mapId,
        featureId,
      },
    )
    this.featureHistory.loadCurrentPage()
  }

  setFeature(feature: IBvFeature, layer: IBvLayer & { singularTitle: string }) {
    this.layer.set(layer)
    const currentFeature = this.feature()
    if (currentFeature && currentFeature.id !== feature.id) {
      this.reset()
    }
    this.feature.set(feature)
    this.popupService.remove(feature.id)
    const controls = this.formControlService.makeControls(feature, layer.id, layer.layerProperties)
    if (!layer.isEditable) {
      controls.forEach((c) => (c.disabled = true))
    }
    this.propertyControls.set(controls)
    const form = this.formControlService.toFormGroup(controls)
    this.form = form
    this.formChangesSub = form.valueChanges.subscribe(() => this.hasUnsavedChanges.set(!!this.getChanges()))
  }

  async onSave() {
    const changes = this.getChanges()
    if (!changes) {
      return
    }
    this.saving.set(true)
    const f = { ...this.feature(), properties: changes }
    if (!f.geometry.coordinates.length) {
      delete f.geometry
    }
    await this.updateFeature(f)
    this.saving.set(false)
    this.form.markAsPristine()
  }

  private getChanges(): Record<string, unknown> | undefined {
    const attachmentControlKeys = this.controls()
      .filter((c) => c.type === EBvFieldType.Attachment)
      .map((c) => c.key)
    const changed = filter(([, control]) => control.dirty, entries(this.form.controls)).filter(([key]) => {
      return !attachmentControlKeys.includes(key)
    })
    if (!changed.length) {
      return
    }

    const changes = {}
    for (const [key, control] of changed) {
      changes[key] = control.value
    }
    return changes
  }

  async updateFeature(f: IBvFeature) {
    try {
      const feature = await firstValueFrom(this.featureService.update(f.id, f))
      this.alertService.success('Changes saved')

      this.hasUnsavedChanges.set(false)

      if (this.layer()) {
        this.setFeature(feature, this.layer())
      }

      this.editorStore.updateMapLayerFeature(feature)
    } catch (error) {
      this.alertService.error('Your changes were not saved')
    }
  }

  async setTaskStatus(orgId, featureId, status: TaskStatus) {
    this.selectedTaskStatus.set(status)
    this.loadTasksForStatus(orgId, featureId, status)
  }

  async loadTasksForStatus(orgId: string, featureId: string, status: TaskStatus) {
    this.loadingTasks.set(true)
    const tasks = await this.getTasks(orgId, featureId, status)
    this.loadingTasks.set(false)
    const ids = tasks.map((t) => t.id)
    this.tasks.update((_tasks) => {
      return _tasks.filter((t) => t.status !== status && !ids.includes(t.id)).concat(tasks)
    })
  }

  async getTasks(orgId: string, featureId: string, status: TaskStatus): Promise<BoardTask[]> {
    const vars = makeVariablesForStatus(orgId, featureId, status)

    return firstValueFrom(
      this._getTasks
        .fetch(
          {
            ...vars,
            first: 1000,
          },
          { fetchPolicy: 'network-only' },
        )
        .pipe(
          rxFilter((res) => !res.errors),
          rxMap((res) => res.data.pagedTasks.nodes as Task[]),
          rxMap((tasks) => tasks.map(taskToBoardTask)),
        ),
    )
  }

  async loadProducts(featureId: string) {
    const products = await firstValueFrom(
      this.getProductTransactions
        .fetch(
          {
            where: {
              transactionType: { eq: ProductItemTransactionType.Consumed },
              consumer: { asset: { id: { eq: featureId } } },
            },
            order: [
              {
                transactionDate: SortEnumType.Desc,
              },
            ],
          },
          { fetchPolicy: 'network-only' },
        )
        .pipe(rxMap((r) => r.data.productItemTransactions.map(toProductItemDetails))),
    )

    this.products.set(products)
  }

  async loadRoutines(orgId, mapId, featureId: string) {
    const { routines: availableRoutines } = await firstValueFrom(
      this.routineService.list(orgId, mapId, {
        applicableTo: featureId,
      }),
    )

    const { routines: appliedRoutines } = await firstValueFrom(
      this.routineService.list(orgId, mapId, {
        appliedTo: featureId,
      }),
    )

    this.routines.set(availableRoutines)
    this.selectedRoutines.set(appliedRoutines)
  }

  async loadComments(featureId) {
    const comments = await this.getComments(featureId)
    this.comments.set(comments)
  }

  private async getComments(featureId): Promise<IBvComment[]> {
    return firstValueFrom(this.featureService.listComments(featureId).pipe(rxMap((arr) => arr.sort(commentSort))))
  }

  async createComment(featureId, value, mentionedUsers: string[] = []) {
    const comment = await firstValueFrom(
      this.featureService.createComment(featureId, {
        featureId,
        value,
        mentionedUsers,
      }),
    )
    this.comments.update((comments) => {
      return comments.concat(comment)
    })
  }

  async deleteComment(featureId, commentId) {
    await firstValueFrom(this.featureService.deleteComment(featureId, commentId))
    this.comments.update((comments) => {
      return comments.filter((c) => c.id !== commentId)
    })
  }

  async onSelectAttachments({ files }, control) {
    try {
      const response = await this.fileAPIService.upload(files, this.feature().id, control.layerPropertyId).toPromise()

      this.alertService.success(`Uploaded`)

      const assets = map((a) => {
        return {
          ...a,
          layerPropertyId: control.layerPropertyId,
        }
      }, response.assets)
      const formControl = this.form.get(control.key)
      const cleaned = filter((a) => !!a.id, formControl.value || [])
      const updated = orderBy(['dateCreated'], ['desc'], [...assets, ...cleaned])
      formControl.setValue(updated)
    } catch (err) {
      console.warn(err)
    }
  }

  async onDeleteAttachment(assetId: string, control: BvCustomFieldControl): Promise<void> {
    await firstValueFrom(this.fileAPIService.remove(assetId))
    const formControl = this.form.get(control.key)
    formControl.setValue(filter((a) => a.id !== assetId, formControl.value))
    this.alertService.success('Attachment deleted')
  }

  async onIdentifiersChange({ systemId, locationIdentifier }) {
    await firstValueFrom(
      this.featureService.update(this.feature().id, {
        systemId,
        locationIdentifier,
      } as IBvFeature),
    )

    this.feature.update((f) => {
      return {
        ...f,
        locationIdentifier,
        systemId,
      }
    })

    this.popupService.remove(this.feature().id)

    this.alertService.success(`Changes saved`)
  }

  async deleteFeature() {
    if (!this.authorizationService.canManageAllFeatures()) {
      return
    }

    const confirmation = await this.getDeleteConfirmation()
    if (!confirmation) {
      return
    }
    const { id, layerId } = this.feature()
    this.editorStore.deleteFeature({ featureId: id, layerId })
  }

  private async getDeleteConfirmation(): Promise<boolean> {
    return new Promise((resolve) => {
      this.dialogService.confirmDialog(
        '',
        `Are you sure you want to delete this? This action cannot be undone.`,
        'Delete',
        'warning',
        undefined,
        (err: unknown, confirm: boolean) => {
          if (!confirm) {
            resolve(false)
            return
          }
          resolve(true)
        },
      )
    })
  }

  async consumeProducts(selections: MutableProductSelection[], assetId: string) {
    const items = flatten(selections.map((s) => s.items)).filter((i) => i.quantity() > 0)
    for (const i of items) {
      const { errors } = await firstValueFrom(
        this.consumeProductItems.mutate({
          consume: {
            consumer: {
              assetId,
            },
            productItemId: i.id,
            quantityConsumed: i.quantity(),
          },
        }),
      )

      if (errors) {
        console.log(errors)
        return
      }

      this.loadProducts(this.feature().id)
    }
  }

  private reset() {
    this.comments.set([])
    this.products.set([])
    this.tasks.set([])
    this.routines.set([])
    this.notifications.set([])
    this.form = undefined
    this.formChangesSub?.unsubscribe()
    this.propertyControls.set([])
    this.hasUnsavedChanges.set(false)
  }
}
